import React from 'react';
import { motion } from 'framer-motion';
import {
  GitBranch, Workflow, FileUp, Users, BadgeCheck, Plug, BarChart3, Sparkles,
  Check, Slack, Mail, Database, FileText, Image as ImageIcon, FileSpreadsheet,
} from 'lucide-react';

/**
 * FeatureGrid — modern SaaS card grid. Each card has an icon, title, a
 * one-line description, and a custom mini-screenshot built from the same
 * Tailwind tokens as the live app so it always feels on-brand.
 */

// ─── Mini "screenshot" panels (one per feature) ────────────────────────────

const MiniConditionalLogic = () => (
  <div className="space-y-1.5 text-[10px]">
    <div className="flex items-center gap-1 text-slate-500">
      <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-mono font-semibold">IF</span>
      <span>Country</span>
      <span className="text-slate-400">=</span>
      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">US</span>
    </div>
    <div className="ml-3 pl-2 border-l-2 border-indigo-200 space-y-1.5">
      <div className="flex items-center gap-1 text-slate-500">
        <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-mono font-semibold">THEN</span>
        <span>Show field</span>
        <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200">State</span>
      </div>
      <div className="flex items-center gap-1 text-slate-500">
        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-mono font-semibold">ELSE</span>
        <span>Hide field</span>
      </div>
    </div>
  </div>
);

const MiniWorkflowAutomation = () => {
  const steps = [
    { Icon: FileText, color: 'bg-indigo-500'  },
    { Icon: GitBranch, color: 'bg-amber-500'   },
    { Icon: Slack,    color: 'bg-emerald-500' },
    { Icon: Mail,     color: 'bg-rose-500'    },
  ];
  return (
    <div className="flex items-center justify-between gap-1">
      {steps.map((s, i) => {
        const Icon = s.Icon;
        return (
          <React.Fragment key={i}>
            <div className={`w-7 h-7 rounded-lg ${s.color} text-white flex items-center justify-center shadow-sm`}>
              <Icon className="w-3.5 h-3.5" />
            </div>
            {i < steps.length - 1 && (
              <div className="flex-1 h-px bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const MiniFileUploads = () => {
  const files = [
    { Icon: ImageIcon,       name: 'profile.jpg',    color: 'text-violet-500',  bg: 'bg-violet-50' },
    { Icon: FileText,        name: 'contract.pdf',   color: 'text-rose-500',    bg: 'bg-rose-50'   },
    { Icon: FileSpreadsheet, name: 'data.xlsx',      color: 'text-emerald-500', bg: 'bg-emerald-50' },
  ];
  return (
    <div className="space-y-1.5">
      {files.map((f, i) => {
        const Icon = f.Icon;
        return (
          <div key={i} className="flex items-center gap-2 text-[10px]">
            <span className={`w-5 h-5 rounded ${f.bg} ${f.color} flex items-center justify-center shrink-0`}>
              <Icon className="w-3 h-3" />
            </span>
            <span className="flex-1 truncate font-medium text-slate-700">{f.name}</span>
            <Check className="w-3 h-3 text-emerald-500" />
          </div>
        );
      })}
    </div>
  );
};

const MiniTeamCollaboration = () => {
  const members = [
    { initials: 'AC', color: 'bg-indigo-500'  },
    { initials: 'MR', color: 'bg-emerald-500' },
    { initials: 'JK', color: 'bg-amber-500'   },
    { initials: 'TP', color: 'bg-rose-500'    },
  ];
  return (
    <div className="space-y-2">
      <div className="flex -space-x-2">
        {members.map((m, i) => (
          <div key={i} className={`w-7 h-7 rounded-full ${m.color} text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white`}>
            {m.initials}
          </div>
        ))}
        <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold flex items-center justify-center ring-2 ring-white">+3</div>
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-600 font-medium">Editor access</span>
        <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">Live</span>
      </div>
    </div>
  );
};

const MiniApprovalFlows = () => {
  const steps = [
    { label: 'Submitted', state: 'done'    },
    { label: 'Manager',   state: 'done'    },
    { label: 'Finance',   state: 'active'  },
    { label: 'Final',     state: 'pending' },
  ];
  return (
    <div className="space-y-1.5">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-2 text-[10px]">
          <div className={`w-4 h-4 rounded-full flex items-center justify-center ring-2 ${
            s.state === 'done'    ? 'bg-emerald-500 ring-emerald-100' :
            s.state === 'active'  ? 'bg-indigo-500  ring-indigo-100  animate-pulse' :
                                    'bg-slate-200   ring-slate-100'
          }`}>
            {s.state === 'done' && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3.5} />}
          </div>
          <span className={s.state === 'pending' ? 'text-slate-400' : 'text-slate-700 font-medium'}>{s.label}</span>
        </div>
      ))}
    </div>
  );
};

const MiniIntegrations = () => {
  const apps = [
    { letter: 'S', bg: 'bg-emerald-500', label: 'Slack'      },
    { letter: 'G', bg: 'bg-blue-500',    label: 'Sheets'     },
    { letter: 'D', bg: 'bg-amber-500',   label: 'Drive'      },
    { letter: 'Z', bg: 'bg-orange-500',  label: 'Zoho'       },
    { letter: 'M', bg: 'bg-indigo-500',  label: 'Mailchimp'  },
    { letter: 'R', bg: 'bg-rose-500',    label: 'Resend'     },
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {apps.map((a, i) => (
        <div key={i} className="flex flex-col items-center gap-0.5">
          <div className={`w-7 h-7 rounded-lg ${a.bg} text-white font-bold text-xs flex items-center justify-center shadow-sm`}>
            {a.letter}
          </div>
          <span className="text-[9px] text-slate-500 font-medium">{a.label}</span>
        </div>
      ))}
    </div>
  );
};

const MiniAnalytics = () => {
  const bars = [40, 65, 50, 80, 55, 90, 70];
  return (
    <div className="space-y-2">
      <div className="flex items-end gap-1 h-12">
        {bars.map((h, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
            className={`flex-1 rounded-t ${i === 5 ? 'bg-indigo-500' : 'bg-indigo-200'}`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-500">Submissions / day</span>
        <span className="font-bold text-emerald-600">+24%</span>
      </div>
    </div>
  );
};

const MiniAIGeneration = () => {
  const lines = ['Customer name', 'Email address', 'Project budget', 'Project timeline'];
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white border border-slate-200 text-[10px]">
        <Sparkles className="w-3 h-3 text-fuchsia-500 shrink-0" />
        <span className="text-slate-700 font-medium truncate">"Build me a client intake form"</span>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="space-y-1.5"
      >
        {lines.map((l, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -8 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.3 + i * 0.12 }}
            className="flex items-center gap-1.5 text-[10px]"
          >
            <span className="w-1 h-1 rounded-full bg-fuchsia-500" />
            <span className="text-slate-600">{l}</span>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

// ─── Feature cards ──────────────────────────────────────────────────────────

const FEATURES = [
  {
    Icon: GitBranch,
    title: 'Conditional Logic',
    desc: 'Show, hide, and route fields based on real-time answers.',
    accent: 'from-indigo-500 to-violet-500',
    Preview: MiniConditionalLogic,
  },
  {
    Icon: Workflow,
    title: 'Workflow Automation',
    desc: 'Trigger emails, approvals, and integrations the moment a form lands.',
    accent: 'from-emerald-500 to-teal-500',
    Preview: MiniWorkflowAutomation,
  },
  {
    Icon: FileUp,
    title: 'File Uploads',
    desc: 'Accept any file — auto-organized in cloud storage or Drive.',
    accent: 'from-violet-500 to-fuchsia-500',
    Preview: MiniFileUploads,
  },
  {
    Icon: Users,
    title: 'Team Collaboration',
    desc: 'Invite teammates with role-based access and shared inboxes.',
    accent: 'from-blue-500 to-sky-500',
    Preview: MiniTeamCollaboration,
  },
  {
    Icon: BadgeCheck,
    title: 'Approval Flows',
    desc: 'Multi-step sign-offs with email reminders and audit trails.',
    accent: 'from-amber-500 to-orange-500',
    Preview: MiniApprovalFlows,
  },
  {
    Icon: Plug,
    title: 'Integrations',
    desc: 'Slack, Sheets, Drive, Mailchimp, Resend, and dozens more.',
    accent: 'from-rose-500 to-pink-500',
    Preview: MiniIntegrations,
  },
  {
    Icon: BarChart3,
    title: 'Analytics',
    desc: 'Track views, conversion rates, drop-off points, and submission trends.',
    accent: 'from-cyan-500 to-blue-500',
    Preview: MiniAnalytics,
  },
  {
    Icon: Sparkles,
    title: 'AI Form Generation',
    desc: 'Describe your form in plain English and let AI build the entire structure.',
    accent: 'from-fuchsia-500 to-pink-500',
    Preview: MiniAIGeneration,
    badge: 'Coming soon',
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.4, delay: 0.05 + i * 0.05 },
  }),
};

export const FeatureGrid = () => {
  return (
    <section
      className="py-20 lg:py-24 bg-white"
      data-testid="feature-grid-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Section heading */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4 }}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
        >
          <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-indigo-600 mb-3">
            Everything you need
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Built for real business workflows
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Powerful features that turn every form into a business outcome — no developer needed.
          </p>
        </motion.div>

        {/* Card grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
          {FEATURES.map((f, i) => {
            const { Icon, title, desc, accent, Preview, badge } = f;
            return (
              <motion.article
                key={title}
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                whileHover={{ y: -4 }}
                className="group relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-xl hover:border-slate-300 transition-all duration-300 overflow-hidden"
                data-testid={`feature-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {/* Gradient accent bar */}
                <span
                  className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${accent} opacity-80 group-hover:opacity-100 transition-opacity`}
                  aria-hidden
                />

                {/* "Coming soon" pill (top-right) */}
                {badge && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700 ring-1 ring-fuchsia-100 text-[10px] font-bold tracking-wide uppercase">
                    <span className="w-1 h-1 rounded-full bg-fuchsia-500 animate-pulse" />
                    {badge}
                  </span>
                )}

                {/* Icon + title row */}
                <div className="flex items-center gap-3 mb-3">
                  <span className={`shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${accent} text-white shadow-md`}>
                    <Icon className="w-5 h-5" strokeWidth={2.2} />
                  </span>
                  <h3 className="text-base font-semibold text-slate-900 leading-tight">
                    {title}
                  </h3>
                </div>

                {/* One-line description */}
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  {desc}
                </p>

                {/* Mini screenshot panel */}
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 group-hover:bg-white group-hover:border-slate-200 transition-colors">
                  <Preview />
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeatureGrid;
